import React from 'react'
import { FormControlLabel, Typography } from '@material-ui/core'

import Accordion from '_/components/accordion'
import SwitchComponent from '_/components/switch'
import Select from '_/components/select'

import { MoneyInput } from '@refera/ui-web'

import useStyles from './styles'
import { useSelector } from 'react-redux'
import { agencySelector } from '_/modules/agency/selectors'
import useRolePermission from '_/hooks/use-role-permission'
import HelpInfoButton from '_/components/button/help-info-button'

export const AgencyBypass = ({ isEditing, ...params }) => {
  const styles = useStyles()
  const { isAdmin, isSAAS } = useRolePermission()

  const agency = useSelector(agencySelector)

  return (
    <Accordion title="Parâmetros da empresa" className={styles.accordion}>
      {isAdmin && (
        <>
          <div>
            <Typography component="h3" className={styles.label}>
              Bypass revisão de orçamento
            </Typography>
            <FormControlLabel
              className={styles.switch}
              control={
                <SwitchComponent
                  name="bypass"
                  checked={params?.values?.bypass}
                  onChange={e => params.setFieldValue('bypass', e.target.checked)}
                  disabled={!isEditing}
                />
              }
            />
          </div>
          <div>
            <Typography component="h3" className={styles.label}>
              Valor mínimo para bypass de orçamento
            </Typography>
            <MoneyInput
              disabled={!isEditing || !params?.values?.bypass}
              allowNegative={false}
              allowLeadingZeros={false}
              placeholder="R$ "
              decimalScale={2}
              name="minBypassValue"
              label=""
              value={params?.values?.minBypassValue}
              onChange={e => params.setFieldValue('minBypassValue', e.floatValue)}
              style={{ fontSize: '14px' }}
            />
            {params.errors?.minBypassValue && (
              <Typography className={styles.error}>{params.errors?.minBypassValue}</Typography>
            )}
          </div>
          <div>
            <Typography component="h3" className={styles.label}>
              Valor máximo para bypass de orçamento
            </Typography>
            <MoneyInput
              disabled={!isEditing || !params?.values?.bypass}
              allowNegative={false}
              allowLeadingZeros={false}
              placeholder="R$ "
              decimalScale={2}
              name="maxBypassValue"
              label=""
              value={params?.values?.maxBypassValue}
              onChange={e => params.setFieldValue('maxBypassValue', e.floatValue)}
              style={{ fontSize: '14px' }}
            />
            {params.errors?.maxBypassValue && (
              <Typography className={styles.error}>{params.errors?.maxBypassValue}</Typography>
            )}
          </div>
        </>
      )}

      {(isAdmin || agency?.contractType === 'SAAS') && (
        <div>
          <Typography component="h3" className={styles.label}>
            Exigir anexos ao abrir chamado
            <HelpInfoButton
              title="Se este campo estiver assinalado, será obrigatório anexar ao menos uma foto ou vídeo ao abrir um chamado."
              placement="bottom"
            />
          </Typography>
          <FormControlLabel
            className={styles.switch}
            defaultValue={params?.values?.requireAttachmentsOnServiceorderOpening ?? false}
            control={
              <SwitchComponent
                name="requireAttachmentsOnServiceorderOpening"
                checked={params?.values?.requireAttachmentsOnServiceorderOpening}
                onChange={e =>
                  params.setFieldValue('requireAttachmentsOnServiceorderOpening', e.target.checked)
                }
                disabled={!isEditing}
              />
            }
          />
        </div>
      )}

      {isAdmin && (
        <div>
          <Typography component="h3" className={styles.label}>
            Opt-in de marketing na abertura do chamado
            <HelpInfoButton
              title={
                'Se este campo estiver assinalado, o formulário público exibirá um aceite ' +
                'para conteúdos e soluções da Refera.'
              }
              placement="bottom"
            />
          </Typography>
          <FormControlLabel
            className={styles.switch}
            defaultValue={params?.values?.enableMarketingOptIn ?? false}
            control={
              <SwitchComponent
                name="enableMarketingOptIn"
                checked={params?.values?.enableMarketingOptIn}
                onChange={e => params.setFieldValue('enableMarketingOptIn', e.target.checked)}
                disabled={!isEditing}
              />
            }
          />
        </div>
      )}

      {agency?.contractType === 'SAAS' && (
        <>
          <div>
            <Typography component="h3" className={styles.label}>
              NF exigida na finalização
              <HelpInfoButton
                title="Se este campo estiver desmarcado, significa que a Nota fiscal NÃO será exigida para que um chamado seja finalizado. Desde que tal chamado não esteja sendo atendido pela Refera."
                placement="bottom"
              />
            </Typography>
            <FormControlLabel
              className={styles.switch}
              defaultValue={params?.values?.nfRequired ?? true}
              control={
                <SwitchComponent
                  name="nfRequired"
                  checked={params?.values?.nfRequired}
                  onChange={e => params.setFieldValue('nfRequired', e.target.checked)}
                  disabled={!isEditing}
                />
              }
            />
          </div>
          {isSAAS && (
            <div>
              <Typography component="h3" className={styles.label}>
                Permite prestador aceitar chamados sem agendar visita
                <HelpInfoButton
                  title="Se este campo estiver assinalado, significa que a intermediária permite que o prestador aceite chamados indicando que fará orçamento à distância"
                  placement="bottom"
                />
              </Typography>
              <FormControlLabel
                className={styles.switch}
                defaultValue={params?.values?.acceptsCallsWithoutVisit ?? false}
                control={
                  <SwitchComponent
                    name="acceptsCallsWithoutVisit"
                    checked={params?.values?.acceptsCallsWithoutVisit}
                    onChange={e =>
                      params.setFieldValue('acceptsCallsWithoutVisit', e.target.checked)
                    }
                    disabled={!isEditing}
                  />
                }
              />
            </div>
          )}
          <div>
            <Typography component="h3" className={styles.label}>
              Permite orçamento simplificado
              <HelpInfoButton
                title="Se este campo estiver marcado, significa que a intermediária permite a criação de orçamentos simplificados"
                placement="bottom"
              />
            </Typography>
            <FormControlLabel
              className={styles.switch}
              control={
                <SwitchComponent
                  name="allowsSimplifiedBudget"
                  checked={params?.values?.allowsSimplifiedBudget}
                  onChange={e => params.setFieldValue('allowsSimplifiedBudget', e.target.checked)}
                  disabled={!isEditing}
                />
              }
            />
          </div>
          <div>
            <Typography component="h3" className={styles.label}>
              Tipo de canal de atendimento
              <HelpInfoButton
                title="Define o canal de comunicação utilizado para interagir com o cliente ao enviar mensagens ao Solicitante, Proprietário ou Pagador na tela de gestão de chamados. 
                Selecione 'WhatsApp Web' para conversas diretas via WhatsApp Web, ou 'Webhook' para integrações automatizadas via API (configuráveis em 'Dados Adicionais' → 'Webhooks')."
                placement="bottom"
              />
            </Typography>
            <Select
              name="channelType"
              label=""
              value={params?.values?.channelType || ''}
              options={[
                { label: 'WhatsApp Web', value: 'whatsapp_web' },
                { label: 'Webhook', value: 'webhook' },
              ]}
              onChange={e => params.setFieldValue('channelType', e.target.value)}
              disabled={!isEditing}
              isLoading={false}
            />
            {params.errors?.channelType && (
              <Typography className={styles.error}>{params.errors?.channelType}</Typography>
            )}
          </div>
        </>
      )}
    </Accordion>
  )
}
